/**
 * SiteScopeSwitcher — top-bar pill that lets the user pick the active site
 * scope ("All sites" or one specific site). Selection is persisted by
 * SiteScopeContext so it survives page navigation + reload.
 *
 * Pattern lifted from Veeva / MasterControl / TrackWise — a tenant-level
 * scope chip in the topbar that filters every list page.
 */

import { useEffect, useRef, useState } from 'react';
import { useSites } from '../../api/orgHooks';
import { useSiteScope, type SiteScope } from '../../auth/SiteScopeContext';
import styles from './SiteScopeSwitcher.module.css';

const KIND_ICON: Record<string, string> = {
  MANUFACTURING_HQ: '🏭',
  MANUFACTURING: '🏭',
  PACKAGING: '📦',
  WAREHOUSE: '🏬',
  LAB_QC: '🧪',
  HEAD_OFFICE: '🏢',
  DISTRIBUTION: '🚚',
};

export function SiteScopeSwitcher() {
  const sitesQ = useSites();
  const { scope, setScope } = useSiteScope();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (popoverRef.current?.contains(e.target as Node)) return;
      if (triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const sites = sitesQ.data?.items ?? [];
  const label = scope.kind === 'all' ? 'All sites' : scope.siteCode;
  const iconBase =
    scope.kind === 'site'
      ? KIND_ICON[sites.find((s) => s.site_id === scope.siteId)?.site_kind ?? ''] ?? '🏭'
      : '🌐';

  function pickAll() {
    setScope({ kind: 'all' } satisfies SiteScope);
    setOpen(false);
  }
  function pickSite(siteId: string, siteCode: string, siteName: string) {
    setScope({ kind: 'site', siteId, siteCode, siteName } satisfies SiteScope);
    setOpen(false);
  }

  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${scope.kind === 'site' ? styles.triggerScoped : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Site scope"
      >
        <span className={styles.icon} aria-hidden>
          {iconBase}
        </span>
        <span className={styles.label}>{label}</span>
        <span className={styles.caret} aria-hidden>
          ▾
        </span>
      </button>

      {open && (
        <div ref={popoverRef} className={styles.popover} role="menu" aria-label="Select site scope">
          <button
            type="button"
            role="menuitem"
            className={`${styles.option} ${scope.kind === 'all' ? styles.optionActive : ''}`}
            onClick={pickAll}
          >
            <span className={styles.optionIcon} aria-hidden>
              🌐
            </span>
            <span className={styles.optionMeta}>
              <span className={styles.optionLabel}>All sites</span>
              <span className={styles.optionSub}>Combined view across every site</span>
            </span>
          </button>

          <div className={styles.divider} aria-hidden />

          {sites.map((s) => {
            const active = scope.kind === 'site' && scope.siteId === s.site_id;
            const icon = KIND_ICON[s.site_kind] ?? '🏭';
            return (
              <button
                key={s.site_id}
                type="button"
                role="menuitem"
                className={`${styles.option} ${active ? styles.optionActive : ''}`}
                onClick={() => pickSite(s.site_id, s.site_code, s.name)}
              >
                <span className={styles.optionIcon} aria-hidden>
                  {icon}
                </span>
                <span className={styles.optionMeta}>
                  <span className={styles.optionLabel}>{s.site_code}</span>
                  <span className={styles.optionSub}>{s.name}</span>
                </span>
                {s.license_expiry && (
                  <span className={styles.optionTrailing}>
                    Lic. {new Date(s.license_expiry).getFullYear()}
                  </span>
                )}
              </button>
            );
          })}

          {sites.length === 0 && (
            <div className={styles.empty}>
              {sitesQ.isLoading ? 'Loading sites…' : 'No sites registered.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
